import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useDialogFocus } from '@/lib/useDialogFocus';

export type CommandAction = {
  /** Stable id. */
  id: string;
  /** What the user reads — verb + object: "Go to Inbox", "New project". */
  label: string;
  /** Optional secondary line for context (e.g. "Workspace · Q4 Launch"). */
  hint?: string;
  /** Section header this action belongs in. */
  section: string;
  /** Optional keyboard shortcut hint (for display only). */
  shortcut?: string;
  /** Fired when activated. The palette closes itself after run resolves. */
  run: () => void | Promise<void>;
  /** Searchable keywords beyond `label` (e.g. ["home", "triage"]). */
  keywords?: string[];
};

interface Props {
  open: boolean;
  onClose: () => void;
  actions: CommandAction[];
  /** Placeholder for the search input. Defaults to "Type a command or search…". */
  placeholder?: string;
}

/**
 * Cmd+K / Ctrl+K command palette — combobox + listbox.
 *
 * Keyboard model (matches Linear / Raycast / VS Code):
 *   - Search input has role=combobox, aria-controls=listbox, aria-activedescendant
 *   - ↓/↑ moves highlight, Enter activates, Esc closes
 *   - Tab cycles focus inside the dialog (useDialogFocus handles trap)
 *   - Mouse hover syncs highlight so keyboard + mouse stay coherent
 *
 * Fuzzy match: lowercased substring of label + keywords + section. Not a true
 * fuzzy ranker — just substring + small bias toward label-prefix matches.
 * Sufficient for ~30 actions; swap for fzf-style if action count balloons.
 */
export function CommandPalette({ open, onClose, actions, placeholder }: Props) {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const panelRef = useDialogFocus<HTMLDivElement>();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions
      .map((a) => {
        const hay = [a.label, a.hint ?? '', a.section, ...(a.keywords ?? [])]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return null;
        // Tiny ranking: label-prefix > label-contains > section/hint/keyword
        const labelLc = a.label.toLowerCase();
        let score = 0;
        if (labelLc.startsWith(q)) score = 100;
        else if (labelLc.includes(q)) score = 50;
        else score = 10;
        return { action: a, score };
      })
      .filter((x): x is { action: CommandAction; score: number } => x !== null)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.action);
  }, [query, actions]);

  // Group filtered list by section, preserving sort order within each section.
  const sections = useMemo(() => {
    const map = new Map<string, CommandAction[]>();
    for (const a of filtered) {
      const arr = map.get(a.section) ?? [];
      arr.push(a);
      map.set(a.section, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);

  // Flat index for keyboard nav (across all sections).
  const flat = useMemo(() => sections.flatMap(([, items]) => items), [sections]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlight(0);
    // useDialogFocus already focuses the first focusable — that's the input.
  }, [open]);

  // Reset highlight when filter changes.
  useEffect(() => {
    setHighlight(0);
  }, [query]);

  // Scroll the highlighted option into view on keyboard nav.
  useEffect(() => {
    if (!open) return;
    optionRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  if (!open) return null;

  function activate(action: CommandAction) {
    Promise.resolve(action.run())
      .catch(() => {
        /* let the action surface its own error */
      })
      .finally(() => {
        onClose();
      });
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => Math.max(0, i - 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setHighlight(flat.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const action = flat[highlight];
      if (action) activate(action);
    }
  }

  const activeId = flat[highlight] ? `cmd-opt-${flat[highlight]!.id}` : undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cmd-palette-title"
      className="fixed inset-0 z-50 grid place-items-start justify-center bg-black/60 px-4 pt-[12vh] animate-in fade-in-0 duration-100"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
        className="w-full max-w-xl overflow-hidden rounded-xl border border-border bg-popover shadow-2xl animate-in zoom-in-95 fade-in-0 duration-100"
      >
        <h2 id="cmd-palette-title" className="sr-only">
          Command palette
        </h2>
        <div className="border-b border-border px-4">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder ?? 'Type a command or search…'}
            autoCapitalize="off"
            spellCheck={false}
            role="combobox"
            aria-expanded
            aria-controls="cmd-palette-listbox"
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            className="block w-full bg-transparent py-4 text-base text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          />
        </div>

        <div
          id="cmd-palette-listbox"
          role="listbox"
          aria-label="Commands"
          className="max-h-[60vh] overflow-y-auto py-2"
        >
          {flat.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No commands match "{query}".
            </div>
          ) : (
            sections.map(([section, items]) => (
              <div key={section} className="mb-2 last:mb-0">
                <div className="px-4 pb-1 pt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {section}
                </div>
                <ul role="presentation">
                  {items.map((action) => {
                    const flatIdx = flat.findIndex((a) => a.id === action.id);
                    const isActive = flatIdx === highlight;
                    return (
                      <li
                        key={action.id}
                        ref={(el) => {
                          optionRefs.current[flatIdx] = el;
                        }}
                        id={`cmd-opt-${action.id}`}
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setHighlight(flatIdx)}
                        onClick={() => activate(action)}
                        className={cn(
                          'flex cursor-pointer items-center justify-between gap-3 px-4 py-2 text-sm',
                          isActive ? 'bg-accent text-foreground' : 'text-foreground/85',
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate">{action.label}</div>
                          {action.hint && (
                            <div className="truncate font-mono text-[10px] text-muted-foreground">
                              {action.hint}
                            </div>
                          )}
                        </div>
                        {action.shortcut && (
                          <kbd className="rounded border border-border bg-card px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                            {action.shortcut}
                          </kbd>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
        <div className="border-t border-border bg-card/40 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <span>↑↓ navigate</span> · <span>↵ select</span> · <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
